import { useSelection } from "@/contexts/SelectionContext";
import { GenerateVideoQuestionSubtitlesDocument } from "@/generated/graphql";
import { toastAiCredits } from "@/lib/utils/aiCredits";
import { getGraphQLErrorMessage, hasGraphQLErrorCode } from "@/lib/utils/graphqlErrors";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { setResponseSubtitles, updateSlide } from "@/store/slideEditorSlice";
import {
  DEFAULT_RESPONSE_SUBTITLE_FONT_SCALE,
  DEFAULT_RESPONSE_SUBTITLE_PRESET,
  DEFAULT_SUBTITLE_OFFSET,
  DEFAULT_SUBTITLE_POSITION,
  ErrorCodes,
  type GrammarResponseSlideData,
  SUBTITLE_FONT_SCALE_MAX,
  SUBTITLE_FONT_SCALE_MIN,
  SUBTITLE_OFFSET_MAX,
  SUBTITLE_OFFSET_MIN,
  SUBTITLE_PRESETS,
  type SubtitlePosition,
  type SubtitlePreset,
  clampSubtitleFontScale,
  clampSubtitleOffset,
} from "@ai-teach/types";
import { useMutation } from "@apollo/client/react";
import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { HiX } from "react-icons/hi";
import { TimeInput } from "./TimeInput";

// Check if [start, end) range overlaps with any subtitle in ranges
function checkRangeCollision(
  start: number,
  end: number,
  ranges: Array<{ startTime: number; endTime: number }>
): boolean {
  return ranges.some((range) => start < range.endTime && end > range.startTime);
}

const POSITIONS: SubtitlePosition[] = ["top", "center", "bottom"];
const FONT_SCALE_STEP = 0.1;
const OFFSET_STEP = 2;

// Video ids auto-generated this session — module scope survives section remounts.
const autoGeneratedVideoIds = new Set<string>();

// `open` toggles visibility only — stays mounted while collapsed so a running generation survives.
export const SubtitleSection = ({ open = true }: { open?: boolean }) => {
  const { slide } = useAppSelector((state) => state.slideEditor.present);
  const responseSlideData = slide?.slideData as GrammarResponseSlideData | undefined;

  const { toggleWarningModal } = useSelection();
  const dispatch = useAppDispatch();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [expandedIndices, setExpandedIndices] = useState<Set<number>>(new Set());

  const videoSrc = responseSlideData?.video?.src ?? null;
  const videoId = responseSlideData?.video?.id ?? null;
  const hasVideo = Boolean(videoSrc);

  const [generateSubtitles, { loading: isGenerating }] = useMutation(
    GenerateVideoQuestionSubtitlesDocument
  );
  const [failed, setFailed] = useState(false);

  const runGenerate = useCallback(
    async (src: string) => {
      const slideId = slide?.id;
      if (!slideId) return;
      setFailed(false);
      try {
        const res = await generateSubtitles({ variables: { slideId, videoSrc: src } });
        const lines = (res.data?.generateVideoQuestionSubtitles ?? []).map((l) => ({
          text: l.text,
          startTime: l.startTime,
          endTime: l.endTime,
        }));
        dispatch(setResponseSubtitles({ slideId, subtitles: lines }));
        toast.success("Subtitles generated!", { id: "vqSubtitleToast" });
      } catch (err) {
        if (hasGraphQLErrorCode(err, ErrorCodes.SUBTITLE_UNAVAILABLE)) {
          // Nothing to caption — not a failure, so no red "retry" banner.
          toast(getGraphQLErrorMessage(err) ?? "This video can't be auto-captioned.", {
            id: "vqSubtitleToast",
            icon: "ℹ️",
          });
        } else {
          setFailed(true);
          if (!toastAiCredits(err, "vqSubtitleToast")) {
            toast.error("Subtitle generation failed", { id: "vqSubtitleToast" });
          }
        }
      }
    },
    [slide?.id, generateSubtitles, dispatch]
  );

  // Ask once per fresh upload — marked handled on the ask itself, not just a "yes".
  const subtitleCount = responseSlideData?.subtitles?.length ?? 0;
  useEffect(() => {
    if (!slide?.id || !videoId || !videoSrc || subtitleCount > 0 || isGenerating) return;
    if (autoGeneratedVideoIds.has(videoId)) return;
    autoGeneratedVideoIds.add(videoId);
    toggleWarningModal("Generate subtitles for the video you just uploaded?", {
      onConfirm: () => void runGenerate(videoSrc),
      onCancel: () => toggleWarningModal(false),
      confirmLabel: "Generate",
      cancelLabel: "Skip",
      title: "Subtitles",
    });
  }, [slide?.id, videoId, videoSrc, subtitleCount, isGenerating, runGenerate, toggleWarningModal]);

  const handleGenerateSubtitles = () => {
    if (videoSrc) void runGenerate(videoSrc);
  };

  if (!slide || !responseSlideData) return null;

  const { subtitles } = responseSlideData;

  const handleAddSubtitle = () => {
    const latestEndTime = subtitles.length > 0 ? Math.max(...subtitles.map((s) => s.endTime)) : 0;
    const startTime = latestEndTime + 1;
    const endTime = startTime + 3;

    dispatch(
      updateSlide({
        slideId: slide.id,
        slideData: {
          ...responseSlideData,
          subtitles: [
            ...responseSlideData.subtitles,
            {
              text: "Subtitle text",
              startTime,
              endTime,
            },
          ],
        },
      })
    );
  };

  const toggleFormatOptions = (index: number) => {
    setExpandedIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const updateSubtitleStyle = (
    index: number,
    patch: Partial<{
      position: string;
      preset: string;
      fontScale: number;
      offset: number;
    }>
  ) => {
    dispatch(
      updateSlide({
        slideId: slide.id,
        slideData: {
          ...responseSlideData,
          subtitles: responseSlideData.subtitles.map((subt, i) =>
            i === index ? { ...subt, ...patch } : subt
          ),
        },
      })
    );
  };

  const applyStyleToAll = (style: {
    position: string;
    preset: string;
    fontScale: number;
    offset: number;
  }) => {
    dispatch(
      updateSlide({
        slideId: slide.id,
        slideData: {
          ...responseSlideData,
          subtitles: responseSlideData.subtitles.map((subt) => ({ ...subt, ...style })),
        },
      })
    );
  };

  return (
    <>
      {/* Stays visible while the section is collapsed so the run isn't silent. */}
      {!open && (isGenerating || failed) && (
        <div
          className={`flex items-center gap-2 text-[11px] font-medium rounded-md px-2.5 py-1.5 ${
            isGenerating
              ? "text-primary-700 bg-primary-50 border border-primary-200"
              : "text-red-600 bg-red-50 border border-red-200"
          }`}
        >
          {isGenerating ? (
            <>
              <span className="inline-block w-3 h-3 rounded-full border-2 border-current border-t-transparent animate-spin" />
              Generating subtitles from the video…
            </>
          ) : (
            "Subtitle generation failed"
          )}
        </div>
      )}
      <div className={open ? "flex flex-col gap-1.5" : "hidden"}>
        {isGenerating && (
          <div className="flex items-center gap-2 text-[11px] font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded-md px-2.5 py-1.5">
            <span className="inline-block w-3 h-3 rounded-full border-2 border-current border-t-transparent animate-spin" />
            Generating subtitles from the video…
          </div>
        )}
        {failed && !isGenerating && (
          <div className="flex items-center justify-between gap-2 text-[11px] font-medium text-red-600 bg-red-50 border border-red-200 rounded-md px-2.5 py-1.5">
            <span>Subtitle generation failed</span>
            {hasVideo && (
              <button
                type="button"
                onClick={handleGenerateSubtitles}
                className="font-semibold underline hover:no-underline"
              >
                Retry
              </button>
            )}
          </div>
        )}
        {subtitles.length > 0 &&
          subtitles.map((sub, index) => {
            const position = (sub.position as SubtitlePosition) ?? DEFAULT_SUBTITLE_POSITION;
            const preset = (sub.preset as SubtitlePreset) ?? DEFAULT_RESPONSE_SUBTITLE_PRESET;
            const fontScale = clampSubtitleFontScale(
              sub.fontScale,
              DEFAULT_RESPONSE_SUBTITLE_FONT_SCALE
            );
            const offset = clampSubtitleOffset(sub.offset);

            return (
              <div
                key={`${sub.startTime}-${index}`}
                className="flex flex-col gap-1.5 p-2 rounded-lg border border-border bg-surface"
              >
                <div className="flex items-center justify-between">
                  <label className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground flex items-center gap-1.5 shrink-0">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500 inline-block" />
                    Text
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      dispatch(
                        updateSlide({
                          slideId: slide.id,
                          slideData: {
                            ...responseSlideData,
                            subtitles: responseSlideData.subtitles.filter((_, i) => i !== index),
                          },
                        })
                      );
                    }}
                    className="text-muted-foreground/40 hover:text-red-400 transition-colors"
                  >
                    <HiX className="text-sm" />
                  </button>
                </div>
                <input
                  type="text"
                  name="text"
                  id={`subtitle-text-${index}`}
                  value={sub.text}
                  onChange={(e) => {
                    dispatch(
                      updateSlide({
                        slideId: slide.id,
                        slideData: {
                          ...responseSlideData,
                          subtitles: responseSlideData.subtitles.map((subt, i) =>
                            i === index ? { ...subt, text: e.target.value } : subt
                          ),
                        },
                      })
                    );
                  }}
                  className="text-xs px-2 py-1.5 bg-surface border border-border rounded focus:border-blue-400 focus:ring-1 focus:ring-blue-100 outline-none placeholder:text-muted-foreground/40"
                  placeholder="Subtitle text..."
                />
                <div className="flex items-center gap-1.5">
                  <label className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground shrink-0">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block" />
                  </label>
                  <div className="flex items-center gap-1">
                    <TimeInput
                      value={sub.startTime}
                      errorMessage={errors[`${index}-start`]}
                      onFocus={() => {
                        setErrors((prev) => {
                          const next = { ...prev };
                          delete next[`${index}-start`];
                          return next;
                        });
                      }}
                      onChange={(second) => {
                        if (second >= sub.endTime) {
                          setErrors((prev) => ({
                            ...prev,
                            [`${index}-start`]: "Must be before end time",
                          }));
                          return;
                        }
                        // Check if new start time would collide with other subtitles
                        const otherSubtitles = subtitles.filter((_item, pos) => pos !== index);
                        if (checkRangeCollision(second, sub.endTime, otherSubtitles)) {
                          setErrors((prev) => ({
                            ...prev,
                            [`${index}-start`]: "Overlaps another subtitle",
                          }));
                          return;
                        }
                        setErrors((prev) => {
                          const next = { ...prev };
                          delete next[`${index}-start`];
                          return next;
                        });
                        const updated = responseSlideData.subtitles.map((subt, i) =>
                          i === index ? { ...subt, startTime: second } : subt
                        );
                        dispatch(
                          updateSlide({
                            slideId: slide.id,
                            slideData: {
                              ...responseSlideData,
                              subtitles: updated.sort((a, b) => a.startTime - b.startTime),
                            },
                          })
                        );
                      }}
                      otherTimestamps={subtitles
                        .filter((_item, pos) => pos !== index)
                        .map((item) => item.startTime)}
                    />
                    <span className="text-muted-foreground/40 text-xs">→</span>
                    <TimeInput
                      value={sub.endTime}
                      errorMessage={errors[`${index}-end`]}
                      onFocus={() => {
                        setErrors((prev) => {
                          const next = { ...prev };
                          delete next[`${index}-end`];
                          return next;
                        });
                      }}
                      onChange={(second) => {
                        if (second <= sub.startTime) {
                          setErrors((prev) => ({
                            ...prev,
                            [`${index}-end`]: "Must be after start time",
                          }));
                          return;
                        }
                        // Check if new end time would collide with other subtitles
                        const otherSubtitles = subtitles.filter((_item, pos) => pos !== index);
                        if (checkRangeCollision(sub.startTime, second, otherSubtitles)) {
                          setErrors((prev) => ({
                            ...prev,
                            [`${index}-end`]: "Overlaps another subtitle",
                          }));
                          return;
                        }
                        setErrors((prev) => {
                          const next = { ...prev };
                          delete next[`${index}-end`];
                          return next;
                        });
                        const updated = responseSlideData.subtitles.map((subt, i) =>
                          i === index ? { ...subt, endTime: second } : subt
                        );
                        dispatch(
                          updateSlide({
                            slideId: slide.id,
                            slideData: {
                              ...responseSlideData,
                              subtitles: updated.sort((a, b) => a.startTime - b.startTime),
                            },
                          })
                        );
                      }}
                      otherTimestamps={subtitles
                        .filter((_item, pos) => pos !== index)
                        .map((item) => item.endTime)}
                    />
                    <button
                      type="button"
                      onClick={() => toggleFormatOptions(index)}
                      className="text-[10px] font-semibold text-primary-600 hover:text-primary-700 ml-auto shrink-0"
                    >
                      {expandedIndices.has(index) ? "Format options −" : "Format options +"}
                    </button>
                  </div>
                </div>

                {expandedIndices.has(index) && (
                  <div className="flex flex-col gap-2 pt-1.5 border-t border-border/50">
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="flex gap-1">
                        {POSITIONS.map((pos) => (
                          <button
                            key={pos}
                            type="button"
                            onClick={() => updateSubtitleStyle(index, { position: pos })}
                            className={`px-2 py-0.5 text-[10px] font-medium rounded-full capitalize transition-all ${
                              position === pos
                                ? "bg-teal-500 text-white"
                                : "text-muted-foreground hover:bg-gray-100"
                            }`}
                          >
                            {pos}
                          </button>
                        ))}
                      </div>

                      <div className="w-px h-4 bg-gray-300 dark:bg-white/10 shrink-0" />

                      <div className="flex gap-1.5 items-center">
                        {(Object.keys(SUBTITLE_PRESETS) as SubtitlePreset[]).map((key) => {
                          const ps = SUBTITLE_PRESETS[key];
                          const isActive = preset === key;
                          return (
                            <button
                              key={key}
                              type="button"
                              title={ps.label}
                              onClick={() => updateSubtitleStyle(index, { preset: key })}
                              style={{
                                backgroundColor:
                                  ps.backgroundColor === "transparent"
                                    ? "rgba(60,60,60,0.8)"
                                    : ps.backgroundColor,
                                boxShadow: isActive
                                  ? `0 0 0 2px ${ps.activeTextColor}`
                                  : "0 0 0 2px transparent",
                              }}
                              className="w-6 h-6 rounded-md flex items-center justify-center text-[10px] font-bold transition-all"
                            >
                              <span style={{ color: ps.activeTextColor }}>A</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="flex items-center gap-1">
                        <span className="text-[10px] font-medium text-muted-foreground">Size</span>
                        <button
                          type="button"
                          disabled={fontScale <= SUBTITLE_FONT_SCALE_MIN}
                          onClick={() =>
                            updateSubtitleStyle(index, {
                              fontScale: clampSubtitleFontScale(fontScale - FONT_SCALE_STEP),
                            })
                          }
                          className="w-5 h-5 flex items-center justify-center rounded text-muted-foreground hover:bg-gray-100 disabled:opacity-30 text-xs"
                        >
                          −
                        </button>
                        <span className="text-[10px] font-mono w-8 text-center">
                          {fontScale.toFixed(1)}×
                        </span>
                        <button
                          type="button"
                          disabled={fontScale >= SUBTITLE_FONT_SCALE_MAX}
                          onClick={() =>
                            updateSubtitleStyle(index, {
                              fontScale: clampSubtitleFontScale(fontScale + FONT_SCALE_STEP),
                            })
                          }
                          className="w-5 h-5 flex items-center justify-center rounded text-muted-foreground hover:bg-gray-100 disabled:opacity-30 text-xs"
                        >
                          +
                        </button>
                      </div>

                      <div className="w-px h-4 bg-gray-300 dark:bg-white/10 shrink-0" />

                      <div className="flex items-center gap-1">
                        <span className="text-[10px] font-medium text-muted-foreground">Nudge</span>
                        <button
                          type="button"
                          disabled={offset <= SUBTITLE_OFFSET_MIN}
                          onClick={() =>
                            updateSubtitleStyle(index, {
                              offset: clampSubtitleOffset(offset - OFFSET_STEP),
                            })
                          }
                          className="w-5 h-5 flex items-center justify-center rounded text-muted-foreground hover:bg-gray-100 disabled:opacity-30 text-xs"
                        >
                          ↑
                        </button>
                        <span className="text-[10px] font-mono w-8 text-center">
                          {offset > 0 ? `+${offset}` : offset}%
                        </span>
                        <button
                          type="button"
                          disabled={offset >= SUBTITLE_OFFSET_MAX}
                          onClick={() =>
                            updateSubtitleStyle(index, {
                              offset: clampSubtitleOffset(offset + OFFSET_STEP),
                            })
                          }
                          className="w-5 h-5 flex items-center justify-center rounded text-muted-foreground hover:bg-gray-100 disabled:opacity-30 text-xs"
                        >
                          ↓
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center justify-end gap-3">
                      {(fontScale !== DEFAULT_RESPONSE_SUBTITLE_FONT_SCALE ||
                        offset !== DEFAULT_SUBTITLE_OFFSET ||
                        position !== DEFAULT_SUBTITLE_POSITION ||
                        preset !== DEFAULT_RESPONSE_SUBTITLE_PRESET) && (
                        <button
                          type="button"
                          onClick={() =>
                            updateSubtitleStyle(index, {
                              position: DEFAULT_SUBTITLE_POSITION,
                              preset: DEFAULT_RESPONSE_SUBTITLE_PRESET,
                              fontScale: DEFAULT_RESPONSE_SUBTITLE_FONT_SCALE,
                              offset: DEFAULT_SUBTITLE_OFFSET,
                            })
                          }
                          className="text-[10px] font-medium text-muted-foreground hover:text-primary-600"
                        >
                          Reset
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={() => applyStyleToAll({ position, preset, fontScale, offset })}
                        className="text-[10px] font-semibold text-primary-600 hover:text-primary-700 border border-dashed border-primary-300 hover:border-primary-400 px-2 py-1 rounded transition-colors"
                      >
                        Apply to all
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

        <div className="flex items-center gap-2 ml-auto">
          <button
            type="button"
            onClick={handleGenerateSubtitles}
            disabled={isGenerating || !hasVideo}
            className="text-[10px] font-semibold text-primary-600 hover:text-primary-700 border border-dashed border-primary-300 hover:border-primary-400 px-2 py-1 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {subtitles.length > 0 ? "Regenerate from video" : "Generate from video"}
          </button>
          <button
            type="button"
            onClick={handleAddSubtitle}
            disabled={!hasVideo || isGenerating}
            className="text-[10px] font-semibold text-primary-600 hover:text-primary-700 border border-dashed border-primary-300 hover:border-primary-400 px-2 py-1 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            + Add subtitle
          </button>
        </div>
      </div>
    </>
  );
};
